import React from 'react';
import {
  Box,
  Paper,
  Typography,
  ButtonBase,
  Tabs,
  Tab,
} from '@mui/material';
import { Settings as SettingsIcon, BarChart3 } from 'lucide-react';
import { WELLNESS_CONFIG_TABS } from './Constants';
import { CandidateTab } from './Containers/Config/CandidateTab';
import { InvitationTab } from './Containers/Config/InvitationTab';
import { SettingsTab } from './Containers/Config/SettingsTab';
import { WellnessScreeningPage } from './Containers/Results/WellnessScreeningPage';
import { useWellness } from './Hooks/useWellness';

interface WellnessTabProps {
  jobOpeningId: string;
}

export const WellnessTab: React.FC<WellnessTabProps> = ({ jobOpeningId }) => {
  const {
    mainTab,
    configTab,
    handleMainTabChange,
    handleConfigTabChange,
    wellnessConfig,
    handleConfigChange,
    handleSaveConfig,
    isLoadingConfig,
    isSavingConfig,
    candidates,
    isLoadingCandidates,
    handleSendInvitation,
    handleSendReminder,
    candidatePagination,
    summaryResults,
    isLoadingResults,
    candidateResults,
    resultPagination,
    candidateSearch,
    setCandidateSearch,
    candidateEmailSearch,
    setCandidateEmailSearch,
    assessmentStatusFilter,
    setAssessmentStatusFilter,
  } = useWellness(jobOpeningId);

  return (
    <Box sx={{ display: 'flex', minHeight: 520, gap: 0 }}>
      {/* Left icon sidebar */}
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          width: 64,
          flexShrink: 0,
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          borderRight: '1px solid',
          borderColor: 'grey.200',
          bgcolor: 'grey.50',
          py: 2,
        }}
      >
        <ButtonBase
          onClick={() => handleMainTabChange('Config')}
          sx={{
            display: 'flex',
            width: 48,
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            borderRadius: 2,
            p: 1,
            transition: 'all 0.2s ease',
            ...(mainTab === 'Config'
              ? {
                  bgcolor: 'primary.main',
                  color: 'common.white',
                  boxShadow: 2,
                }
              : {
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: 'background.paper',
                    color: 'primary.main',
                  },
                }),
          }}
        >
          <SettingsIcon size={20} />
          <Typography
            variant='caption'
            sx={{
              fontSize: '0.5rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              color: 'inherit',
            }}
          >
            Config
          </Typography>
        </ButtonBase>

        <ButtonBase
          onClick={() => handleMainTabChange('Results')}
          sx={{
            display: 'flex',
            width: 48,
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            borderRadius: 2,
            p: 1,
            transition: 'all 0.2s ease',
            ...(mainTab === 'Results'
              ? {
                  bgcolor: 'primary.main',
                  color: 'common.white',
                  boxShadow: 2,
                }
              : {
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: 'background.paper',
                    color: 'primary.main',
                  },
                }),
          }}
        >
          <BarChart3 size={20} />
          <Typography
            variant='caption'
            sx={{
              fontSize: '0.5rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              color: 'inherit',
            }}
          >
            Results
          </Typography>
        </ButtonBase>
      </Paper>

      {/* Main content area */}
      <Box sx={{ minWidth: 0, flex: 1, overflowY: 'auto', p: 3, maxHeight: 'calc(100vh - 160px)' }}>
        {mainTab === 'Config' && (
          <>
            <Tabs
              value={configTab}
              onChange={(_, val) => handleConfigTabChange(val)}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5 }}
            >
              {WELLNESS_CONFIG_TABS.map((tab) => (
                <Tab
                  key={tab}
                  label={tab}
                  value={tab}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                />
              ))}
            </Tabs>

            {configTab === 'Settings' && (
              <SettingsTab
                config={wellnessConfig}
                onChange={handleConfigChange}
                onSave={handleSaveConfig}
                isSaving={isSavingConfig || isLoadingConfig}
              />
            )}

            {configTab === 'Invitation Template' && (
              <InvitationTab
                emailTemplate={wellnessConfig.emailTemplate ?? ''}
                onChange={(updated) => handleConfigChange({ ...wellnessConfig, emailTemplate: updated })}
                onSave={handleSaveConfig}
                isSaving={isSavingConfig}
              />
            )}

            {configTab === 'Candidates' && (
              <CandidateTab
                jobOpeningId={jobOpeningId}
                candidates={candidates}
                isLoading={isLoadingCandidates}
                onSendInvitation={handleSendInvitation}
                onSendReminder={handleSendReminder}
                onViewResults={() => handleMainTabChange('Results')}
                pagination={candidatePagination}
                search={candidateSearch}
                onSearchChange={setCandidateSearch}
                emailSearch={candidateEmailSearch}
                onEmailSearchChange={setCandidateEmailSearch}
                assessmentStatusFilter={assessmentStatusFilter}
                onAssessmentStatusFilterChange={setAssessmentStatusFilter}
              />
            )}
          </>
        )}

        {mainTab === 'Results' && (
          <WellnessScreeningPage
            summary={summaryResults}
            candidateResults={candidateResults}
            isLoading={isLoadingResults}
            pagination={resultPagination}
          />
        )}
      </Box>
    </Box>
  );
};
